import { faArrowLeft, faDiagramProject, faHouse, faScrewdriverWrench, faUserClock, faWrench } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Form, Input, Modal, Select, Space } from "antd"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { useEffect, useState } from "react"
import VSpace from "../common/VSpace"
import { CustomerDetail } from "./customerApi"
import {
  buildCreateJobRequest,
  canCreateJob,
  createJobStepTitle,
  CreateNewType,
  CreateNewTypes,
  jobRedirectUrl,
  jobRouteSegment,
  needsCreateJobStep,
  propertiesRedirectUrl,
  shouldShowPropertySelection,
} from "./createNewJobModel"
import { useCreateJobMutation } from "./customerQueries"
import useServiceTicketsEnabled from "../../hooks/useServiceTicketsEnabled"
import { jobNameMaxLength } from "../../domain/jobs/jobName"
import useFeatureFlag from "../../hooks/useFeatureFlag"
import { JOB_DETAILS_CLEANUP_FEATURE } from "../../domain/jobs/jobs"

const typeChoices: { type: CreateNewType; label: string; icon: IconDefinition }[] = [
  { type: CreateNewTypes.Project, label: 'Project', icon: faDiagramProject },
  { type: CreateNewTypes.ServiceTicket, label: 'Service Ticket', icon: faScrewdriverWrench },
  { type: CreateNewTypes.MaintenanceContract, label: 'Maintenance Contract', icon: faWrench },
  { type: CreateNewTypes.Consultation, label: 'Consultation', icon: faUserClock },
  { type: CreateNewTypes.Property, label: 'Property', icon: faHouse },
]

type Props = {
  open: boolean
  customer: CustomerDetail
  onClose: () => void
  onCreateConsultation: () => void
  navigate?: (url: string) => void
}

const defaultNavigate = (url: string) => { window.location.href = url }

const CreateNewJobModal = ({ open, customer, onClose, onCreateConsultation, navigate = defaultNavigate }: Props) => {
  const showServiceTickets = useServiceTicketsEnabled()
  const jobDetailsCleanup = useFeatureFlag(JOB_DETAILS_CLEANUP_FEATURE)
  const availableTypeChoices = showServiceTickets
    ? typeChoices
    : typeChoices.filter(choice => choice.type !== CreateNewTypes.ServiceTicket)
  const [pendingType, setPendingType] = useState<CreateNewType | null>(null)
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | undefined>()
  const [jobName, setJobName] = useState('')
  const createJobMutation = useCreateJobMutation()

  const propertyList = customer.propertyList ?? []

  useEffect(() => {
    if (open) {
      setPendingType(null)
      setSelectedPropertyId(propertyList[0]?.id)
      setJobName('')
    }
  }, [open])

  const createJob = (type: CreateNewType, propertyId?: number) => {
    const segment = jobRouteSegment(type)
    if (!segment) {
      return
    }
    createJobMutation.mutate(
      { type: segment, request: buildCreateJobRequest(type, customer.id, propertyId, jobName) },
      {
        onSuccess: created => {
          onClose()
          navigate(jobRedirectUrl(segment, created.url))
        },
      }
    )
  }

  const chooseType = (type: CreateNewType) => {
    if (type === CreateNewTypes.Consultation) {
      onClose()
      onCreateConsultation()
      return
    }
    if (type === CreateNewTypes.Property) {
      onClose()
      navigate(propertiesRedirectUrl(customer.url))
      return
    }
    if (needsCreateJobStep(type, customer.properties, jobDetailsCleanup)) {
      setPendingType(type)
      return
    }
    createJob(type, propertyList[0]?.id)
  }

  const showPropertySelection = shouldShowPropertySelection(pendingType, customer.properties)

  const propertyOptions = propertyList.map(p => ({ label: p.address1, value: p.id }))

  return <Modal
    open={open}
    title={createJobStepTitle(pendingType, jobDetailsCleanup)}
    onCancel={onClose}
    footer={pendingType
      ? [
          <Button key="back" icon={<FontAwesomeIcon icon={faArrowLeft} />} onClick={() => setPendingType(null)}>Back</Button>,
          <Button
            key="create"
            type="primary"
            loading={createJobMutation.isLoading}
            disabled={!canCreateJob({ nameRequired: jobDetailsCleanup, jobName, showPropertySelection, selectedPropertyId })}
            onClick={() => createJob(pendingType, showPropertySelection ? selectedPropertyId : propertyList[0]?.id)}
          >
            Create
          </Button>,
        ]
      : null}
  >
    {pendingType
      ? <Form layout="vertical">
          {jobDetailsCleanup && <Form.Item label="Name" htmlFor="create-job-name" required>
            <Input id="create-job-name" value={jobName} maxLength={jobNameMaxLength} autoFocus
              placeholder="e.g. Smith backyard patio" onChange={e => setJobName(e.target.value)} />
          </Form.Item>}
          {showPropertySelection && <Form.Item label="Property" htmlFor="create-job-property">
            <Select
              id="create-job-property"
              value={selectedPropertyId}
              options={propertyOptions}
              onChange={setSelectedPropertyId}
              style={{ width: '100%' }}
            />
          </Form.Item>}
        </Form>
      : <VSpace>
          {availableTypeChoices.map(choice => <Button
            key={choice.type}
            block
            size="large"
            style={{ textAlign: 'left' }}
            loading={createJobMutation.isLoading}
            onClick={() => chooseType(choice.type)}
            icon={<FontAwesomeIcon icon={choice.icon} />}
          >
            <Space>{choice.label}</Space>
          </Button>)}
        </VSpace>}
  </Modal>
}

export default CreateNewJobModal
